import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { get } from 'services/network'
import {
  getAccessToken,
  getOrgIdFromStorage,
  getOrgNameFromStorage,
  setOrganizationInStorage,
} from 'utils/common'
import AuthScreen from 'components/sign-in/AuthScreen'
import Logo from 'assets/images/login/logo.svg'
import ChevronIcon from 'assets/images/login/chevron.svg'
import 'containers/sigin-in/SignIn.scss'

function SelectEvent() {
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [selectedEventId, setSelectedEventId] = useState(
    () => getOrgIdFromStorage() || ''
  )
  const [loading, setLoading] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  const fetchOrganizations = async () => {
    setLoading(true)
    try {
      const response = await get('organization', false, null, null, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      const payload = Array.isArray(response)
        ? response
        : response?.organizations || response?.data
      const normalizedEvents = Array.isArray(payload)
        ? payload
            .filter((item) => item.enabled !== false)
            .map((item) => ({
              org_id: String(item.organizationId || item.id || ''),
              org_name:
                item.org_name ||
                item.name ||
                item.organizationName ||
                'Organization',
            }))
            .filter((item) => Boolean(item.org_id))
        : []
      setEvents(normalizedEvents)
      const storedOrgId = getOrgIdFromStorage()
      if (storedOrgId && !getOrgNameFromStorage()) {
        const match = normalizedEvents.find((item) => item.org_id === storedOrgId)
        if (match?.org_name) {
          setOrganizationInStorage(storedOrgId, match.org_name)
        }
      }
    } catch (error) {
      setEvents([])
    }
    setLoading(false)
  }
  const onContinue = () => {
    if (!selectedEventId) return
    setOrganizationInStorage(
      selectedEventId,
      selectedOrganization?.org_name || 'Organization'
    )
    navigate('/events', { replace: true })
  }

  useEffect(() => {
    fetchOrganizations()
  }, [])

  useEffect(() => {
    if (!menuOpen) return undefined

    const closeOnOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setMenuOpen(false)
      }
    }
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [menuOpen])

  const selectedOrganization = events.find(
    (item) => item.org_id === selectedEventId
  )
  const placeholder = loading
    ? 'Loading organizations...'
    : 'Select an organization'

  return (
    <AuthScreen>
      <div className='login-card'>
        <div className='login-card-header'>
          <img className='login-logo' src={Logo} width={175} height={50} alt='XLR8' />
          <div className='login-card-copy'>
            <h1>Select Organization</h1>
            <p>Choose an organization to continue.</p>
          </div>
        </div>
        <div className='login-divider' />
        <div className='login-form'>
          <div className='login-field'>
            <label htmlFor='event-select'>
              Organization<span>*</span>
            </label>
            <div
              className={`org-select${menuOpen ? ' org-select--open' : ''}`}
              ref={menuRef}
            >
              <button
                id='event-select'
                type='button'
                className='org-select__trigger'
                aria-haspopup='listbox'
                aria-expanded={menuOpen}
                disabled={loading}
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span
                  className={
                    selectedOrganization
                      ? 'org-select__value'
                      : 'org-select__value org-select__value--placeholder'
                  }
                >
                  {selectedOrganization?.org_name || placeholder}
                </span>
                <img
                  className={
                    menuOpen
                      ? 'org-select__chevron org-select__chevron--open'
                      : 'org-select__chevron'
                  }
                  src={ChevronIcon}
                  width={20}
                  height={20}
                  alt=''
                />
              </button>
              {menuOpen && (
                <div className='org-select__menu' role='listbox'>
                  {events.length === 0 ? (
                    <p className='org-select__empty'>No organizations found</p>
                  ) : (
                    events.map((event) => {
                      const isSelected = event.org_id === selectedEventId
                      return (
                        <button
                          key={event.org_id}
                          type='button'
                          role='option'
                          aria-selected={isSelected}
                          className={
                            isSelected
                              ? 'org-select__option org-select__option--selected'
                              : 'org-select__option'
                          }
                          onClick={() => {
                            setSelectedEventId(event.org_id)
                            setMenuOpen(false)
                          }}
                        >
                          <span>{event.org_name}</span>
                          {isSelected && (
                            <svg
                              width='16'
                              height='16'
                              viewBox='0 0 16 16'
                              fill='none'
                              aria-hidden='true'
                            >
                              <path
                                d='M3.5 8.2 6.4 11l6.1-6.2'
                                stroke='#006c35'
                                strokeWidth='1.6'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          )}
                        </button>
                      )
                    })
                  )}
                </div>
              )}
            </div>
          </div>
          <button
            type='button'
            className='login-submit'
            disabled={!selectedEventId || loading}
            onClick={onContinue}
          >
            Continue
          </button>
        </div>
      </div>
    </AuthScreen>
  )
}

export default SelectEvent
